import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext.tsx';
import { apiGet, apiPut } from '../../lib/api.ts';
import { LoadingSpinner } from '../../components/ui/Shared.tsx';
import type { PublicUser } from '@runninglime/shared';

export default function BillingShipping() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profile, setProfile] = useState<PublicUser | null>(null);

  const [vatNumber, setVatNumber] = useState('');
  const [sdiNumber, setSdiNumber] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('IT');

  useEffect(() => {
    const load = async () => {
      const res = await apiGet<PublicUser>('/profile');
      if (res.success && res.data) {
        setProfile(res.data);
        if (res.data.billingProfile) {
          const bp = res.data.billingProfile;
          setVatNumber(bp.vatNumber || '');
          setSdiNumber(bp.sdiNumber || '');
          setStreet(bp.shippingAddress?.street || '');
          setCity(bp.shippingAddress?.city || '');
          setProvince(bp.shippingAddress?.province || '');
          setPostalCode(bp.shippingAddress?.postalCode || '');
          setCountry(bp.shippingAddress?.country || 'IT');
        }
      }
      setLoading(false);
    };
    load();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    const res = await apiPut<PublicUser>('/profile/billing', {
      vatNumber,
      sdiNumber,
      shippingAddress: { street, city, province, postalCode, country },
    });

    if (res.success && res.data) {
      setSuccess('Profile saved successfully! Your account is now pending admin verification.');
      setProfile(res.data);
      // Force a page reload to refresh auth context with new verification status
      setTimeout(() => window.location.reload(), 1500);
    } else {
      setError(res.error || 'Failed to save profile');
    }
    setSaving(false);
  };

  if (loading) return <LoadingSpinner />;

  const isRejected = profile?.verificationStatus === 'rejected';
  const isPendingVerification = profile?.verificationStatus === 'pending_verification';

  return (
    <div className="max-w-2xl mx-auto py-10 px-4 animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-dark mb-2">Billing & Shipping</h1>
        <p className="text-gray-500">
          {isPendingVerification
            ? 'Your profile is pending admin verification. You can still update your information below.'
            : 'Complete your billing and shipping information to get started.'}
        </p>
      </div>

      {isRejected && profile?.rejectionReason && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h3 className="font-semibold text-red-800">Verification Rejected</h3>
              <p className="text-red-700 text-sm mt-1">{profile.rejectionReason}</p>
              <p className="text-red-600 text-sm mt-2">Please fix the issues and resubmit your profile.</p>
            </div>
          </div>
        </div>
      )}

      {isPendingVerification && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="font-semibold text-amber-800">Pending Verification</h3>
              <p className="text-amber-700 text-sm mt-1">
                Your profile is being reviewed by our team. You can browse the store and place orders in the meantime — they will be processed once your account is verified.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
        {/* Billing Section */}
        <div>
          <h2 className="text-lg font-semibold text-dark mb-4 flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-lime/20 flex items-center justify-center text-sm font-bold text-dark">1</span>
            Billing Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number *</label>
              <input
                type="text"
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value)}
                placeholder="e.g. IT12345678901"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-lime focus:border-transparent outline-none transition-all"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SDI Number *</label>
              <input
                type="text"
                value={sdiNumber}
                onChange={(e) => setSdiNumber(e.target.value)}
                placeholder="e.g. ABCDEFG"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-lime focus:border-transparent outline-none transition-all"
                required
              />
            </div>
          </div>
        </div>

        {/* Shipping Section */}
        <div>
          <h2 className="text-lg font-semibold text-dark mb-4 flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-lime/20 flex items-center justify-center text-sm font-bold text-dark">2</span>
            Shipping Address
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
              <input
                type="text"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                placeholder="Via Roma 1"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-lime focus:border-transparent outline-none transition-all"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Milano"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-lime focus:border-transparent outline-none transition-all"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Province *</label>
                <input
                  type="text"
                  value={province}
                  onChange={(e) => setProvince(e.target.value)}
                  placeholder="MI"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-lime focus:border-transparent outline-none transition-all"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code *</label>
                <input
                  type="text"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="20100"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-lime focus:border-transparent outline-none transition-all"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="IT"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-lime focus:border-transparent outline-none transition-all"
                required
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm">
            {success}
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          className="w-full btn-gradient py-3 rounded-xl font-semibold text-dark disabled:opacity-50 active:scale-[0.98] transition-transform"
        >
          {saving ? 'Saving...' : 'Save & Submit for Verification'}
        </button>
      </form>
    </div>
  );
}
